import React, { useRef, useState, useCallback } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { Upload, X, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const API = `${window.location.origin}/api`;

/**
 * Drag-and-drop image uploader with client-side compression (1600px / 80% JPEG).
 * Persists to Emergent Object Storage via POST /api/files/upload (auth).
 * Designed for mobile: the visible "Take photo" button uses `capture="environment"`
 * so Mark/Sandy can snap directly from the site.
 *
 * Props:
 *   value: string[] of image URLs already uploaded
 *   onChange: (newValue: string[]) => void
 *   authHeaders: axios request config with Authorization header
 *   folder: storage folder name (default "gallery")
 *   maxPhotos: hard cap on the array length (default 20)
 */
export const ImageUploader = ({
  value = [],
  onChange,
  authHeaders,
  folder = "gallery",
  maxPhotos = 20,
  testId = "image-uploader",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const compressImage = async (file) => {
    const isHeic = /\.(heic|heif)$/i.test(file.name || "") || /heic|heif/i.test(file.type || "");
    // Browsers (except Safari) can't decode HEIC — skip client compression and
    // let the server convert it to JPEG.
    if (isHeic || !file.type.startsWith("image/")) return file;
    try {
      return await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1600,
        useWebWorker: true,
        initialQuality: 0.8,
        fileType: file.type === "image/png" ? "image/png" : "image/jpeg",
      });
    } catch (e) {
      // If compression fails, use the original so the upload still succeeds
      console.warn("[ImageUploader] compression failed:", e);
      return file;
    }
  };

  const uploadOne = async (file) => {
    const compressed = await compressImage(file);
    const form = new FormData();
    form.append("file", compressed, compressed.name || file.name);
    const res = await axios.post(`${API}/files/upload?folder=${encodeURIComponent(folder)}`, form, {
      headers: { ...(authHeaders?.headers || {}), "Content-Type": "multipart/form-data" },
    });
    return res.data.url;
  };

  const handleFiles = useCallback(async (files) => {
    let fileArr = Array.from(files || []);
    if (!fileArr.length) return;
    // Single-photo slots (e.g. hero image): new upload REPLACES the current photo.
    const replacing = maxPhotos === 1;
    if (replacing) {
      fileArr = fileArr.slice(0, 1);
    } else {
      const remaining = maxPhotos - value.length;
      if (remaining <= 0) {
        toast.error(`Maximum ${maxPhotos} photos allowed — remove one first.`);
        return;
      }
      if (fileArr.length > remaining) {
        toast.info(`Only room for ${remaining} more photo${remaining > 1 ? "s" : ""} (max ${maxPhotos}) — uploading the first ${remaining}.`);
        fileArr = fileArr.slice(0, remaining);
      }
    }
    setUploading(true);
    setProgress({ done: 0, total: fileArr.length });
    const newUrls = [];
    for (let i = 0; i < fileArr.length; i += 1) {
      try {
        const url = await uploadOne(fileArr[i]);
        newUrls.push(url);
      } catch (e) {
        console.error("[ImageUploader] upload failed:", e);
        const detail = e.response?.data?.detail || (e.response ? `server error ${e.response.status}` : "network error — check your connection");
        toast.error(`Upload failed: ${fileArr[i].name} (${detail})`);
      }
      setProgress({ done: i + 1, total: fileArr.length });
    }
    setUploading(false);
    if (newUrls.length) {
      onChange?.(replacing ? newUrls : [...value, ...newUrls]);
      toast.success(replacing && value.length ? "Photo replaced." : `${newUrls.length} photo${newUrls.length > 1 ? "s" : ""} uploaded.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onChange, authHeaders, folder, maxPhotos]);

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer?.files);
  };

  const removePhoto = (url) => {
    onChange?.(value.filter((u) => u !== url));
  };

  const movePhoto = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= value.length) return;
    const next = [...value];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange?.(next);
  };

  return (
    <div className="space-y-3" data-testid="image-uploader" data-uploader-slot={testId}>
      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50 bg-muted/20"}`}
        data-testid={`${testId}-dropzone`}
      >
        <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm font-medium">Drag &amp; drop photos here</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {maxPhotos === 1
            ? "or tap to browse · a new photo replaces the current one · iPhone (HEIC) supported"
            : "or tap to browse · select several at once · iPhone (HEIC) photos supported"}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          hidden
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid={`${testId}-input`}
        />
      </div>

      {/* Mobile camera shortcut */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="flex-1 gap-2"
          onClick={() => cameraInputRef.current?.click()}
          data-testid={`${testId}-camera-btn`}
        >
          <Camera className="h-4 w-4" />
          Take photo
        </Button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          capture="environment"
          hidden
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid={`${testId}-camera-input`}
        />
      </div>

      {/* Uploading indicator */}
      {uploading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={`${testId}-progress`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Uploading {progress.done}/{progress.total}…
        </div>
      )}

      {/* Thumbnail grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testId}-grid`}>
          {value.map((url, idx) => (
            <div key={url} className="relative group aspect-square rounded-md overflow-hidden border bg-muted">
              <img
                src={url.startsWith("http") ? url : `${window.location.origin}${url}`}
                alt={`gallery ${idx + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => removePhoto(url)}
                className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                title="Remove"
                data-testid={`${testId}-remove-${idx}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="absolute bottom-1 left-1 flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                {idx > 0 && (
                  <button type="button" onClick={() => movePhoto(idx, idx - 1)} className="bg-black/70 text-white rounded w-5 h-5 text-xs">‹</button>
                )}
                {idx < value.length - 1 && (
                  <button type="button" onClick={() => movePhoto(idx, idx + 1)} className="bg-black/70 text-white rounded w-5 h-5 text-xs">›</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
